import upperFirst from 'lodash/upperFirst';
import { memo } from 'react';
import styled from 'styled-components';

import colors from '@front/common/src/utils/colors';
import { getCurrentCountry } from '@front/common/src/utils/country';

import type { SinglePrestationsDetailsCleaningProps } from '@front/customer/src/components/basket/types';
import { CONFIG_BY_COUNTRY } from '@front/customer/src/constants/country-config';
import { useIsITCSelectedOrSuccessfullyPaired } from '@front/customer/src/hooks/use-is-itc-selected-or-successfully-paired';
import { getFormattedPrice } from '@front/customer/src/utils/cleaning';

import { translate } from '@front/i18n';

import Accordion from '@front/web-ui/src/components/accordion/accordion';
import Text from '@front/web-ui/src/components/base-text/variants/text';
import Separator from '@front/web-ui/src/components/separator/separator';

import useBasketSection from '../../../../../../hooks/use-basket-section';
import { route } from '../../../../../../steps/steps';
import PromoCode from '../../../../../promo-code/promo-code';
import ServiceFeeIncludedText from '../../../../common/sections/prestations/service-fee-included-text';
import Category from '../common/category';
import { CesuDeductionAlert } from '../common/cesu-deduction-alert';
import AccordionSubtitlePrice from '../common/prices/accordion-subtitle-price';
import { AccordionTitlePrice } from '../common/prices/accordion-title-price';
import { PriceLines } from '../common/prices/price-lines';
import Surcharges from '../common/surcharges';
import { TaxCreditChoiceAlert } from '../common/tax-credit-choice-alert';
import SingleReductions from './single-reductions';

const t = translate('basket.cleaning.services');

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 0 20px;
`;

const StyledAccordion = styled(Accordion)`
  margin-top: 20px;
`;

const SinglePrestationsDetails = ({
  cleaningPrestation,
  cleaningPrestationOptions,
  duration,
  hasPets,
  isLoadingPrices,
  mode,
  prices,
  promotion,
  shouldShowPromoCode,
  universeReference,
  user,
}: SinglePrestationsDetailsCleaningProps) => {
  const { isTaxCreditAvailable } = CONFIG_BY_COUNTRY[getCurrentCountry()];
  const {
    isWecasaCarePairingSuccessful,
    isInstantTaxCreditSelected,
    isITCSelectedOrSuccessfullyPaired,
    shouldShowTaxCreditPriceInfo,
  } = useIsITCSelectedOrSuccessfullyPaired({ user, isCesuEnabled: true });

  const hasPrestations = !!cleaningPrestation;
  const hasDuration = !!duration;

  const formattedPriceAfterTax = getFormattedPrice(hasDuration, prices?.after_tax);
  const formattedTotalPrice = getFormattedPrice(hasDuration, prices?.total);
  const formattedTaxAmount = getFormattedPrice(hasDuration, prices?.tax_credit);

  const { isOpen, isVisible } = useBasketSection({
    data: hasPrestations,
    sectionLocations: [route('prestations'), route('frequency'), route('duration'), route('options'), route('animals')],
  });

  if (!isVisible) return null;

  return (
    <Container>
      <Separator />
      <StyledAccordion
        testId='prestations-prices-accordion'
        icon='basket'
        iconLabel={t('basket.services.icon_label.basket')}
        title={upperFirst(t('universes.cleaning.title'))}
        titlePreview={
          <AccordionTitlePrice
            formattedPrice={formattedTotalPrice}
            isITCSelectedOrSuccessfullyPaired={isITCSelectedOrSuccessfullyPaired}
            isLoadingPrices={isLoadingPrices}
          />
        }
        subtitle={
          <AccordionSubtitlePrice
            formattedTotalPriceAfterTax={formattedPriceAfterTax}
            isITCSelectedOrSuccessfullyPaired={isITCSelectedOrSuccessfullyPaired}
            isLoadingPrices={isLoadingPrices}
            shouldShowTaxCreditLines={isTaxCreditAvailable && shouldShowTaxCreditPriceInfo}
          />
        }
        subtitleVisibility='close'
        isOpen={isOpen}
      >
        {hasPrestations ? (
          <>
            <Category
              cleaningPrestation={cleaningPrestation}
              cleaningPrestationOptions={cleaningPrestationOptions}
              duration={duration}
              hasPets={hasPets}
              frequencyText={t('.frequency.spot')}
            />
            <Surcharges surcharges={prices?.surcharges} />
            <SingleReductions hasDuration={hasDuration} prices={prices} />
            <PriceLines
              formattedTaxAmount={formattedTaxAmount}
              formattedTotalPrice={formattedTotalPrice}
              formattedPriceAfterTax={formattedPriceAfterTax}
              isITCSelectedOrSuccessfullyPaired={isITCSelectedOrSuccessfullyPaired}
              isLoading={isLoadingPrices}
              shouldShowTaxCreditLines={isTaxCreditAvailable && shouldShowTaxCreditPriceInfo}
            />
            <ServiceFeeIncludedText />
          </>
        ) : (
          <Text.SM color={colors.ink50}>{t('.no_services')}</Text.SM>
        )}
      </StyledAccordion>
      {isTaxCreditAvailable && shouldShowTaxCreditPriceInfo && (
        <TaxCreditChoiceAlert
          isInstantTaxCreditSelected={isInstantTaxCreditSelected}
          isWecasaCarePairingSuccessful={isWecasaCarePairingSuccessful}
        />
      )}
      {mode === 'creation' && hasPrestations && shouldShowPromoCode && (
        <PromoCode
          cleaningPrestation={cleaningPrestation}
          promotion={promotion}
          totalPrice={prices?.total.price || 0}
          universeReference={universeReference}
          user={user}
        />
      )}
      <CesuDeductionAlert totalPrice={prices?.total.price} />
    </Container>
  );
};

export default memo(SinglePrestationsDetails);
